import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  Wallet, Shield, TrendingUp, CreditCard, Coins, ArrowDown, ArrowUp,
  AlertTriangle, CheckCircle, ExternalLink, RefreshCw, Clock, Percent,
  ArrowLeft, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import WalletConnectModal from '../components/WalletConnectModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditFacility = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { 
    account, 
    bethBalance,
    isConnecting,
    isMainnet,
    contractAddresses
  } = useWeb3();

  const [activeTab, setActiveTab] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [termDays, setTermDays] = useState('90');
  const [isLoading, setIsLoading] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [position, setPosition] = useState({
    collateral_amount: 0,
    credit_utilized: 0,
    current_ltv: 0,
    max_borrowable: 0,
    is_active: false
  });
  const [transactions, setTransactions] = useState([]);

  // Fetch position data from backend
  const fetchPosition = async () => {
    if (!account || !token) return;
    
    try {
      const res = await fetch(`${API}/collateral/position/${account}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosition(data);
      }
    } catch (error) {
      console.error('Error fetching position:', error);
    }
  };

  // Fetch transaction history
  const fetchTransactions = async () => {
    if (!account || !token) return;
    
    try {
      const res = await fetch(`${API}/collateral/transactions/${account}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  useEffect(() => {
    if (account && token) {
      fetchPosition();
      fetchTransactions();
    }
  }, [account, token]);

  const formatNumber = (num) => {
    if (!num) return '0';
    return parseFloat(num).toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/collateral/deposit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          wallet_address: account
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Deposited ${amount} BETH as collateral!`);
        setAmount('');
        setPosition(data.position);
        fetchTransactions();
      } else {
        toast.error(data.detail || 'Deposit failed');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error('Deposit failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/collateral/withdraw`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          wallet_address: account
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Withdrew ${amount} BETH from collateral!`);
        setAmount('');
        setPosition(data.position);
        fetchTransactions();
      } else {
        toast.error(data.detail || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdraw error:', error);
      toast.error('Withdrawal failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawCredit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/credit/draw`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          wallet_address: account,
          term_days: parseInt(termDays)
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Drew ${amount} BETH credit for ${termDays} days!`);
        setAmount('');
        setPosition(data.position);
        fetchTransactions();
      } else {
        toast.error(data.detail || 'Failed to draw credit');
      }
    } catch (error) {
      console.error('Draw credit error:', error);
      toast.error('Failed to draw credit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRepay = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/credit/repay`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          wallet_address: account
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Repaid ${amount} BETH credit!`);
        setAmount('');
        setPosition(data.position);
        fetchTransactions();
      } else {
        toast.error(data.detail || 'Repayment failed');
      }
    } catch (error) {
      console.error('Repay error:', error);
      toast.error('Repayment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getLTVColor = (ltv) => {
    if (ltv >= 85) return 'text-red-500';
    if (ltv >= 70) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getTransactionIcon = (type) => {
    switch(type) {
      case 'deposit': return <ArrowDown className="w-4 h-4 text-green-400" />;
      case 'withdraw': return <ArrowUp className="w-4 h-4 text-red-400" />;
      case 'credit_draw': return <TrendingUp className="w-4 h-4 text-blue-400" />;
      case 'credit_repay': return <CheckCircle className="w-4 h-4 text-green-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="credit-facility-page">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Credit Facility</h1>
              <p className="text-gray-400 text-sm">Deposit BETH collateral and draw credit against your position</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {account ? (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { fetchPosition(); fetchTransactions(); }}
                  className="border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <div className="px-3 py-2 bg-[#1a2d4a] rounded-lg text-sm text-gray-300 border border-[#2a3d5a]">
                  {account.slice(0, 6)}...{account.slice(-4)}
                </div>
              </div>
            ) : (
              <Button onClick={() => setShowWalletModal(true)} disabled={isConnecting} className="bg-[#b8860b] hover:bg-[#9a7209]">
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Network Warning */}
        {account && !isMainnet && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-white font-medium">Wrong Network</p>
              <p className="text-sm text-gray-400">Please switch to Ethereum Mainnet</p>
            </div>
          </div>
        )}

        {/* Connect Wallet Prompt */}
        {!account && (
          <div className="mb-6 p-8 bg-[#0d1d35] border border-[#1a2d4a] rounded-xl text-center">
            <Wallet className="w-12 h-12 text-[#b8860b] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Connect Your Wallet</h3>
            <p className="text-gray-400 mb-4">Connect MetaMask or Trust Wallet to deposit BETH collateral and access credit</p>
            <Button onClick={() => setShowWalletModal(true)} disabled={isConnecting} className="bg-[#b8860b] hover:bg-[#9a7209]">
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </Button>
          </div>
        )}

        {account && (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">BETH Balance</span>
                    <Coins className="w-4 h-4 text-[#b8860b]" />
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(bethBalance)}</p>
                  <p className="text-xs text-gray-500">In your wallet</p>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Collateral Deposited</span>
                    <Shield className="w-4 h-4 text-[#b8860b]" />
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(position.collateral_amount)}</p>
                  <p className="text-xs text-gray-500">BETH locked in vault</p>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Credit Available</span>
                    <CreditCard className="w-4 h-4 text-green-400" />
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(position.max_borrowable)}</p>
                  <p className="text-xs text-gray-500">Max borrowable at 75% LTV</p>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Current LTV</span>
                    <Percent className="w-4 h-4 text-[#b8860b]" />
                  </div>
                  <p className={`text-2xl font-bold ${getLTVColor(position.current_ltv)}`}>
                    {position.current_ltv.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500">Liquidation at 85%</p>
                </CardContent>
              </Card>
            </div>

            {/* Active Credit Alert */}
            {position.credit_utilized > 0 && (
              <Card className="bg-gradient-to-r from-[#b8860b]/20 to-[#0d1d35] border-[#b8860b]/30 mb-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#b8860b]" />
                    Active Credit Line
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-400">Principal</p>
                      <p className="text-lg font-bold text-white">{formatNumber(position.credit_utilized)} BETH</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">APR</p>
                      <p className="text-lg font-bold text-[#b8860b]">8.0%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Collateral</p>
                      <p className="text-lg font-bold text-white">{formatNumber(position.collateral_amount)} BETH</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Health Factor</p>
                      <p className={`text-lg font-bold ${position.current_ltv < 70 ? 'text-green-400' : position.current_ltv < 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {position.current_ltv < 70 ? 'Healthy' : position.current_ltv < 80 ? 'Moderate' : 'At Risk'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Tabs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Collateral Management */}
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white">Collateral Management</CardTitle>
                  <CardDescription>Deposit or withdraw BETH collateral</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-6">
                    <Button 
                      variant={activeTab === 'deposit' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('deposit')}
                      className={activeTab === 'deposit' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]'}
                    >
                      <ArrowDown className="w-4 h-4 mr-2" />
                      Deposit
                    </Button>
                    <Button 
                      variant={activeTab === 'withdraw' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('withdraw')}
                      className={activeTab === 'withdraw' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]'}
                    >
                      <ArrowUp className="w-4 h-4 mr-2" />
                      Withdraw
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400 block mb-2">Amount (BETH)</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        data-testid="collateral-amount-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {activeTab === 'deposit' 
                          ? `Wallet Balance: ${formatNumber(bethBalance)} BETH`
                          : `Available to withdraw: ${formatNumber(position.collateral_amount - (position.credit_utilized / 0.75))} BETH`
                        }
                      </p>
                    </div>

                    <Button 
                      className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                      onClick={activeTab === 'deposit' ? handleDeposit : handleWithdraw}
                      disabled={isLoading || !amount}
                      data-testid={activeTab === 'deposit' ? 'deposit-btn' : 'withdraw-btn'}
                    >
                      {isLoading ? 'Processing...' : activeTab === 'deposit' ? 'Deposit Collateral' : 'Withdraw Collateral'}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      Minimum deposit: 100 BETH • Max LTV: 75%
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Credit Management */}
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white">Credit Management</CardTitle>
                  <CardDescription>Draw credit or repay your loan</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-6">
                    <Button 
                      variant={activeTab === 'borrow' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('borrow')}
                      className={activeTab === 'borrow' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]'}
                      disabled={position.collateral_amount <= 0}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Borrow
                    </Button>
                    <Button 
                      variant={activeTab === 'repay' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('repay')}
                      className={activeTab === 'repay' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]'}
                      disabled={position.credit_utilized <= 0}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Repay
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400 block mb-2">Amount (BETH)</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        data-testid="credit-amount-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {activeTab === 'borrow' 
                          ? `Max borrowable: ${formatNumber(position.max_borrowable)} BETH`
                          : `Outstanding credit: ${formatNumber(position.credit_utilized)} BETH`
                        }
                      </p>
                    </div>

                    {activeTab === 'borrow' && (
                      <div>
                        <label className="text-sm text-gray-400 block mb-2">Loan Term</label>
                        <select
                          value={termDays}
                          onChange={(e) => setTermDays(e.target.value)}
                          className="w-full bg-[#1a2d4a] border border-[#2a3d5a] rounded-md px-3 py-2 text-white"
                          data-testid="term-select"
                        >
                          <option value="30">30 Days</option>
                          <option value="90">90 Days</option>
                          <option value="180">180 Days</option>
                          <option value="365">365 Days</option>
                        </select>
                      </div>
                    )}

                    <Button 
                      className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                      onClick={activeTab === 'borrow' ? handleDrawCredit : handleRepay}
                      disabled={isLoading || !amount || (activeTab === 'borrow' && position.collateral_amount <= 0)}
                      data-testid={activeTab === 'borrow' ? 'borrow-btn' : 'repay-btn'}
                    >
                      {isLoading ? 'Processing...' : activeTab === 'borrow' ? 'Draw Credit' : 'Repay Credit'}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      APR: 8% • Liquidation threshold: 85% LTV
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transaction History */}
            {transactions.length > 0 && (
              <Card className="bg-[#0d1d35] border-[#1a2d4a] mt-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-[#b8860b]" />
                    Transaction History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {transactions.slice(0, 10).map((tx, index) => (
                      <div key={tx.tx_id || index} className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getTransactionIcon(tx.type)}
                          <div>
                            <p className="text-white text-sm font-medium capitalize">
                              {tx.type.replace('_', ' ')}
                            </p>
                            <p className="text-gray-500 text-xs">
                              {new Date(tx.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${tx.type === 'deposit' || tx.type === 'credit_repay' ? 'text-green-400' : 'text-white'}`}>
                            {tx.type === 'withdraw' || tx.type === 'credit_draw' ? '-' : '+'}{formatNumber(tx.amount)} BETH
                          </p>
                          <p className="text-gray-500 text-xs font-mono">{tx.tx_id}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contract Links */}
            <div className="mt-8 p-4 bg-[#0d1d35] border border-[#1a2d4a] rounded-xl">
              <p className="text-sm text-gray-400 mb-2">Contract Addresses (Mainnet)</p>
              <div className="flex flex-wrap gap-4 text-xs">
                <a 
                  href={`https://etherscan.io/address/${contractAddresses?.BETH_TOKEN || '0x6822824c5349bf005e353ec5dda3fa34595ea811'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#b8860b] hover:text-[#d4a10a] flex items-center gap-1"
                >
                  BETH Token <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href={`https://etherscan.io/address/${contractAddresses?.COLLATERAL_VAULT || '0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#b8860b] hover:text-[#d4a10a] flex items-center gap-1"
                >
                  Collateral Vault <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href={`https://etherscan.io/address/${contractAddresses?.CREDIT_LINE || '0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#b8860b] hover:text-[#d4a10a] flex items-center gap-1"
                >
                  Credit Line <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Wallet Connect Modal */}
      <WalletConnectModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)} 
      />
    </div>
  );
};

export default CreditFacility;
